import {
  SpeakingSessionExaminerAvailabilityDto,
  SpeakingSessionRoomStructureAssignedExaminerDto,
  SpeakingSlotType,
} from 'api/planning';
import { useMemo } from 'react';
import { calculateExaminerAvailability } from './utils/examinersAvailabilityCalculator';
import { getStructureDateTimeRange } from './utils/getStructureDateTimeRange';
import { ExaminerAvailabilityFilterType } from './ManageExaminerToRoomStructureFilterBar';
import {
  AvailabilityDisplay,
  getExaminerAvailabilityDisplay,
} from './utils/examinerAvailabilityDisplay';
import { useExaminerAvailabilities } from '../contexts/ExaminerAvailabilitiesContext';
import { getStructureSlotRange } from '../structures/utils/getStructureSlotRange';
import {
  calculatePeriodsLeft,
  calculatePeriodsRight,
  RoomClashPeriodDto,
} from '../structures/utils/calculateClashedPeriods';

type Props = {
  speakingSessionDate?: string;
  structureStartTime?: string;
  structureEndTime?: string;
  speakingSlotType: SpeakingSlotType;
  assignedExaminer?: SpeakingSessionRoomStructureAssignedExaminerDto;
  clashedPeriods: RoomClashPeriodDto[];
};

export type RoomStructureAssignedExaminerWithAvailability = {
  examinerId: number;
  firstName?: string;
  lastName?: string;
  phoneNumber?: string;
  email?: string;
  examinerNumber?: string;
  availabilities?: SpeakingSessionExaminerAvailabilityDto[];
  unavailabilities?: SpeakingSessionExaminerAvailabilityDto[];
  availability?: AvailabilityDisplay;
};

export const useRoomStructureExaminerAllocation = ({
  speakingSessionDate,
  structureStartTime,
  structureEndTime,
  speakingSlotType,
  assignedExaminer,
  clashedPeriods,
}: Props): {
  periodsLeft: { shift: number; isClashed: boolean }[];
  periodsRight: { shift: number; isClashed: boolean }[];
  structureSlotsCount: number;
  isApplicable: boolean;
  assignedExaminerWithAvailability?: RoomStructureAssignedExaminerWithAvailability;
} => {
  const allDaySlotsCount = 72;
  const {
    startSlot: shiftLeft,
    endSlot: adjustedStructureEndSlot,
    slotsCount: structureSlotsCount,
  } = getStructureSlotRange(structureStartTime, structureEndTime, allDaySlotsCount);

  const periodsLeft = useMemo(
    () => calculatePeriodsLeft(shiftLeft, clashedPeriods),
    [shiftLeft, clashedPeriods]
  );

  const periodsRight = useMemo(
    () => calculatePeriodsRight(adjustedStructureEndSlot, allDaySlotsCount, clashedPeriods),
    [adjustedStructureEndSlot, clashedPeriods, allDaySlotsCount]
  );

  const isApplicable =
    speakingSlotType === SpeakingSlotType.IPA || speakingSlotType === SpeakingSlotType.IPC;

  const isExaminerAssigned = assignedExaminer != null && assignedExaminer != undefined;

  const { examinersAvailabilities, isFetchingAvailabilities } = useExaminerAvailabilities();

  const examinerAvailability = useMemo(() => {
    if (!isExaminerAssigned) return undefined;
    if (
      isFetchingAvailabilities ||
      !speakingSessionDate ||
      !structureStartTime ||
      !structureEndTime
    ) {
      return {
        availability: getExaminerAvailabilityDisplay(ExaminerAvailabilityFilterType.NotProvided),
        availabilities: [],
        unavailabilities: [],
      };
    }

    const { start: structureStartDateTime, end: structureEndDateTime } = getStructureDateTimeRange(
      speakingSessionDate,
      structureStartTime,
      structureEndTime
    );

    const calculatedExaminerAvailability = calculateExaminerAvailability(
      assignedExaminer.examinerId,
      examinersAvailabilities || [],
      structureStartDateTime,
      structureEndDateTime
    );

    return {
      availability: getExaminerAvailabilityDisplay(calculatedExaminerAvailability.availability),
      availabilities: calculatedExaminerAvailability.availabilities,
      unavailabilities: calculatedExaminerAvailability.unavailabilities,
    };
  }, [
    isExaminerAssigned,
    isFetchingAvailabilities,
    speakingSessionDate,
    assignedExaminer?.examinerId,
    structureStartTime,
    structureEndTime,
    examinersAvailabilities,
  ]);

  return {
    periodsLeft,
    periodsRight,
    structureSlotsCount,
    isApplicable,
    assignedExaminerWithAvailability: isExaminerAssigned
      ? {
          examinerId: assignedExaminer.examinerId,
          firstName: assignedExaminer.firstName,
          lastName: assignedExaminer.lastName,
          phoneNumber: assignedExaminer.phoneNumber,
          email: assignedExaminer.email,
          examinerNumber: assignedExaminer.examinerNumber,
          availability: examinerAvailability?.availability,
          availabilities: examinerAvailability?.availabilities,
          unavailabilities: examinerAvailability?.unavailabilities,
        }
      : undefined,
  };
};
